// Small formatting helpers the screens share. Money is integer minor units with its currency, formatted by the
// venue formatter; a time is shown on the venue's clock. A player's data spans venues, so a screen that shows one
// venue's figures makes a formatter for that venue's currency and timezone rather than the frame's default.

import { createVenueFormatter, type VenueFormatter } from '@clavian/i18n';
import { useEffect, useState } from 'react';

import { DEFAULT_VENUE_LOCALE } from '../i18n';

/** A formatter for one currency and timezone (a wallet's organisation, a receipt's venue). */
export function formatterFor(currency: string, timeZone: string = DEFAULT_VENUE_LOCALE.timeZone): VenueFormatter {
  return createVenueFormatter({ locale: DEFAULT_VENUE_LOCALE.locale, timeZone, currency });
}

/** `2:05:09` under a day, for a countdown. */
export function clock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/** `1 h 20 m`, `45 m`: a duration in words-light, for a list. */
export function shortDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds / 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} m`;
  return m === 0 ? `${h} h` : `${h} h ${m} m`;
}

/**
 * Seconds left, counted down locally from what the server said at `serverTime`. The server's clock is the
 * reference: the offset between it and this device is taken once per answer, so a phone whose clock is wrong
 * still counts the right time. Ticks every second while `running`.
 */
export function useCountdown(timeLeftSeconds: number | undefined, serverTime: string | undefined, running: boolean): number | undefined {
  const [now, setNow] = useState(() => Date.now());
  const key = `${serverTime ?? ''}|${timeLeftSeconds ?? ''}`;
  const [anchor, setAnchor] = useState<{ readonly key: string; readonly left: number; readonly at: number } | undefined>(undefined);
  // A fresh answer from the server (it is as of `serverTime`, and reached this device just now) re-anchors the count.
  if (timeLeftSeconds !== undefined && anchor?.key !== key) setAnchor({ key, left: timeLeftSeconds, at: now });

  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  if (timeLeftSeconds === undefined) return undefined;
  if (anchor === undefined || anchor.key !== key) return timeLeftSeconds;
  if (!running) return anchor.left;
  return Math.max(0, anchor.left - Math.floor((now - anchor.at) / 1000));
}
